// =============================================================================
// InvoiceView.tsx
//
// The page the homeowner lands on when they click the invoice link in their
// email or text. URL: /i/:token
//
// Anon-accessible. Resolves the token via the SECURITY DEFINER RPC
// resolve_invoice_token(), then renders:
//   - Bardin header + balance hero
//   - PDF preview (iframe of signed URL)
//   - Pay options: Stripe card/ACH button + "Pay by check" instructions
//   - Reply-to-Shannon contact prompt
//
// Jul 15, 2026 — partial payments (Shannon: homeowners couldn't pay less than
// the full balance). "Pay full balance" / "Pay a different amount" chooser;
// the chosen amount goes to supplement-invoice-checkout v2, which flags it
// invoice_partial so the webhook decrements balance_due instead of stamping
// the invoice paid-in-full. Progress ("$X paid so far") comes back from
// resolve_invoice_token as partial_paid_total.
//
// Jul 28, 2026 — card processing fee (Greg: card payments through this link
// were charged the bare amount with no surcharge, so Bardin ate the Stripe fee
// and had to invoice it separately). ACH / Card method chooser; card payments
// over $1,500 show the 3% fee, add it to the total, and charge amount + fee in
// one Stripe checkout (supplement-invoice-checkout v3). Same tier policy as
// the homeowner portal: ≤ $1,500 = no fee on either method.
//
// Oct 20, 2026 — bank payments typed in by hand. A homeowner who keys in
// their account and routing numbers has to confirm Stripe's test deposit
// before the payment goes through (10 days, then Stripe cancels it). This
// page used to call that payment "in transit, no need to pay it again", so
// nobody finished. resolve_invoice_token now flags it (needs_verification +
// Stripe's verify link) and the page asks them to verify instead. The amount
// still comes off the balance shown, so they're never asked to pay twice.
//
// If already paid: shows a green "Paid in full" confirmation.
// If revoked / not found: shows a friendly error and a help phone number.
// =============================================================================

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabaseAnon as supabase, SUPABASE_URL } from "../lib/supabase";
import { generateSupplementInvoicePDF, type InvoiceDeal } from "../lib/supplementInvoicePDF";
import {
  daysLeftLabel,
  daysUntil,
  depositInstructions,
  depositShouldBeThere,
  fmtVerifyDay,
  fmtVerifyDeadline,
  type MicrodepositType,
} from "../lib/bankVerification";

// Anon client comes from lib/supabase.ts so we inherit its hardcoded URL/key
// fallback. Rolling our own createClient() here with `import.meta.env.X!`
// caused the entire app to white-screen on May 13, 2026 when builds ran
// without .env.local available — Vite substituted `undefined` and Supabase
// threw "supabaseUrl is required" at module-load time.

type ResolvedInvoice = {
  ok: boolean;
  reason?: string;
  delivery_id?: string;
  homeowner_name?: string;
  address?: string;
  insurance_company?: string | null;
  invoice_number?: string | null;
  invoice_amount?: number;
  supplement_approved_amount?: number;
  balance_due?: number;
  partial_paid_total?: number;
  /** Unsettled homeowner ACH (deal_payments: stripe + state 'made'), from
   *  resolve_invoice_token v2. Netted out of the balance shown and charged so
   *  a homeowner mid-settlement is never asked to pay the same money twice. */
  pending_payments?: {
    amount: number;
    made_at: string | null;
    // Oct 20, 2026: still waiting on the homeowner's bank verification.
    needs_verification?: boolean;
    verify_url?: string | null;
    microdeposit_type?: MicrodepositType;
    deposit_arrival_at?: string | null;
    verify_deadline_at?: string | null;
  }[];
  pending_total?: number;
  pdf_storage_path?: string;
  sent_at?: string;
  paid_at?: string | null;
  paid_amount?: number | null;
  paid_via?: string | null;
};

const money = (v: number | null | undefined) =>
  Number(v ?? 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

// Card surcharge tier — keep in sync with supplement-invoice-checkout and
// create-payment-link (the server recomputes; this is display only).
const CARD_SURCHARGE_BPS = 300;         // 3.00%
const FEE_FREE_CEILING_CENTS = 150_000; // ≤ $1,500 = no fee on either method

export default function InvoiceView() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ResolvedInvoice | null>(null);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [creatingCheckout, setCreatingCheckout] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [payChoice, setPayChoice] = useState<"full" | "custom">("full");
  const [customAmount, setCustomAmount] = useState("");
  const [payMethod, setPayMethod] = useState<"ach" | "card">("ach");
  // Stripe redirects back with ?paid=1&partial=1 after a partial payment —
  // the balance updates via webhook a moment later (or days later for ACH),
  // so show a "payment received" note instead of relying on the row.
  const justPaidPartial = new URLSearchParams(window.location.search).get("partial") === "1";

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: rpc } = await supabase.rpc("resolve_invoice_token", { p_token: token });
        if (cancelled) return;
        const parsed = rpc as ResolvedInvoice;
        setData(parsed);
        if (parsed?.ok && parsed.pdf_storage_path) {
          const { data: url } = await supabase.storage
            .from("deal-documents")
            .createSignedUrl(parsed.pdf_storage_path, 3600);
          if (!cancelled) setSignedUrl(url?.signedUrl ?? null);
        }
      } catch (e) {
        // swallow — show "not_found" state
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Parsed custom amount in dollars, or null when invalid / out of range.
  // Aug 14, 2026 — subtract payments the homeowner has already authorised that
  // have not settled yet. Before this, someone who paid by ACH still saw the
  // full balance and a live Pay button for the next 1-4 business days, which is
  // how at least two homeowners ended up submitting the same payment twice.
  // Safe against double-counting: a payment leaves state 'made' at settlement,
  // the same moment the webhook decrements balance_due.
  const pendingTotal = Number(data?.pending_total ?? 0);
  const balanceDue = Math.max(0, Number(data?.balance_due ?? 0) - pendingTotal);
  // Pending payments still waiting on the homeowner to verify their bank
  // account (Oct 20, 2026). Credited above like the rest, but not "in transit".
  const verifyPending = (data?.pending_payments ?? []).filter((p) => p.needs_verification);
  const verifyTotal = verifyPending.reduce((s, p) => s + Number(p.amount ?? 0), 0);
  const transitTotal = Math.max(0, pendingTotal - verifyTotal);
  const parsedCustom = (() => {
    if (payChoice !== "custom") return null;
    const n = Number(customAmount.replace(/[$,\s]/g, ""));
    if (!Number.isFinite(n)) return null;
    const cents = Math.round(n * 100);
    if (cents < 100 || cents > Math.round(balanceDue * 100)) return null;
    return cents / 100;
  })();
  const payAmount = payChoice === "custom" ? parsedCustom : balanceDue;
  const customInvalid = payChoice === "custom" && customAmount.trim() !== "" && parsedCustom === null;

  // Card surcharge preview — mirrors the server's tier (card + > $1,500 = 3%).
  const baseCents = payAmount !== null ? Math.round(payAmount * 100) : 0;
  const feeCents = payMethod === "card" && baseCents > FEE_FREE_CEILING_CENTS
    ? Math.round((baseCents * CARD_SURCHARGE_BPS) / 10_000)
    : 0;
  const totalCents = baseCents + feeCents;
  const feeFreeForThisAmount = baseCents > 0 && baseCents <= FEE_FREE_CEILING_CENTS;

  const handlePayOnline = async () => {
    if (!token || creatingCheckout) return;
    if (payChoice === "custom" && parsedCustom === null) return;
    setCreatingCheckout(true);
    try {
      // Defers to a separate edge function `supplement-invoice-checkout`
      // that creates a Stripe Checkout Session with metadata.deal_id +
      // metadata.invoice_token, and returns { url }.
      // (Stripe webhook v10+ already in place; will call mark_invoice_paid RPC
      // — or apply_invoice_partial_payment for partial amounts — when it sees
      // these metadata fields on a checkout.session.completed event.)
      const resp = await fetch(
        `${SUPABASE_URL}/functions/v1/supplement-invoice-checkout`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(
            payChoice === "custom" && parsedCustom !== null && parsedCustom < balanceDue
              ? { token, amount: parsedCustom, payment_method: payMethod }
              : { token, payment_method: payMethod },
          ),
        },
      );
      const json = await resp.json();
      if (json?.url) {
        window.location.href = json.url;
      } else {
        alert("We couldn't create the secure pay session. Please reply to your invoice email or text — we'll sort it out fast.");
      }
    } catch (e) {
      alert("Something went wrong. Reply to your invoice email or text us at (470) 823-8816.");
    } finally {
      setCreatingCheckout(false);
    }
  };

  const handleDownloadPDF = async () => {
    // If a signed URL already exists (PDF was previously generated), download it
    if (signedUrl) {
      const a = document.createElement("a");
      a.href = signedUrl;
      a.download = `Bardin-Roofing-Invoice-${data?.invoice_number ?? "download"}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }

    // No PDF exists yet — generate one on the fly client-side
    if (!data || generatingPdf) return;
    setGeneratingPdf(true);
    try {
      const dealData: InvoiceDeal = {
        id: data.delivery_id ?? "",
        homeowner_name: data.homeowner_name ?? null,
        homeowner_email: null,
        homeowner_phone: null,
        property_street: data.address ?? null,
        property_city: null,
        property_state: null,
        property_zip: null,
        insurance_company: data.insurance_company ?? null,
        final_invoice_amount: data.invoice_amount ?? 0,
        supplement_invoice_amount: data.supplement_approved_amount ?? 0,
        supplement_approved_amount: data.supplement_approved_amount ?? 0,
        post_build_balance_remaining: data.balance_due ?? 0,
        pending_payments: (data.pending_payments ?? []).map((p) => ({
          amount: Number(p.amount ?? 0),
          label: p.needs_verification
            ? "Payment pending - waiting on bank account verification"
            : p.made_at
            ? "Payment pending - bank transfer submitted " +
              new Date(p.made_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
            : "Payment pending - bank transfer clearing",
        })),
      };
      const pdfBytes = await generateSupplementInvoicePDF(dealData, data.invoice_number ?? undefined);
      const blob = new Blob([pdfBytes as BlobPart], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Bardin-Roofing-Invoice-${data.invoice_number ?? "download"}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Couldn't generate the invoice PDF. Please reply to your invoice email for a copy.");
    } finally {
      setGeneratingPdf(false);
    }
  };

  // --- loading ---
  if (loading) {
    return (
      <div style={layoutStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: "center", padding: 40, color: "#5b6266" }}>Loading your invoice…</div>
        </div>
        <style>{cssReset}</style>
      </div>
    );
  }

  // --- not found / revoked ---
  if (!data?.ok) {
    return (
      <div style={layoutStyle}>
        <div style={cardStyle}>
          <Header />
          <div style={{ padding: 32, textAlign: "center" }}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>This invoice link isn't active</div>
            <div style={{ color: "#5b6266", fontSize: 14, marginBottom: 18 }}>
              {data?.reason === "not_found"
                ? "We couldn't find an invoice for this link. It may have been replaced with a newer version."
                : "This invoice link has expired or is no longer valid."}
            </div>
            <div style={{ color: "#2a2e30", fontSize: 14 }}>
              Reply to your most recent invoice email or text us at <b>(470) 823-8816</b> and we'll send a fresh link right over.
            </div>
          </div>
        </div>
        <style>{cssReset}</style>
      </div>
    );
  }

  const paid = !!data.paid_at;

  return (
    <div style={layoutStyle}>
      <div style={cardStyle}>
        <Header />

        <div style={{ padding: "24px 28px 28px" }}>
          <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5, color: "#5b6266", fontWeight: 700 }}>
            Invoice for
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4 }}>{data.homeowner_name}</div>
          <div style={{ fontSize: 13, color: "#5b6266", marginTop: 2 }}>{data.address}</div>
          {data.invoice_number && (
            <div style={{ fontSize: 12, color: "#5b6266", marginTop: 8 }}>
              Invoice #: <b style={{ color: "#2a2e30", fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>{data.invoice_number}</b>
            </div>
          )}
          {data.insurance_company && (
            <div style={{ fontSize: 12, color: "#5b6266", marginTop: 4 }}>
              Insurance carrier: <b style={{ color: "#2a2e30" }}>{data.insurance_company}</b>
            </div>
          )}

          {/* Balance hero */}
          {paid ? (
            <div style={paidHeroStyle}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.85)", textTransform: "uppercase", letterSpacing: 0.5 }}>
                Paid in full
              </div>
              <div style={{ fontSize: 30, fontWeight: 800, color: "white", marginTop: 6 }}>
                {money(data.paid_amount ?? data.balance_due)} <span style={{ fontSize: 14, fontWeight: 600, opacity: 0.85 }}> · {data.paid_via}</span>
              </div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.85)", marginTop: 8 }}>
                Received {new Date(data.paid_at!).toLocaleDateString("en-US", { dateStyle: "long" })} — thank you!
              </div>
            </div>
          ) : (
            <div style={balanceHeroStyle}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#9aa0a4", textTransform: "uppercase", letterSpacing: 0.5 }}>
                Balance due
              </div>
              <div style={{ fontSize: 38, fontWeight: 800, color: "#f46530", marginTop: 6, letterSpacing: -1 }}>
                {money(balanceDue)}
              </div>
              <div style={{ fontSize: 13, color: "#9aa0a4", marginTop: 4 }}>
                Final invoice {money(data.invoice_amount)}
                {data.supplement_approved_amount && data.supplement_approved_amount > 0 ? ` · includes ${money(data.supplement_approved_amount)} approved supplements` : ""}
              </div>
              {verifyTotal > 0 && (
                <div style={{ fontSize: 14, fontWeight: 700, color: "#fbbf24", marginTop: 6 }}>
                  {money(verifyTotal)} payment waiting on your bank verification — already credited above.
                  <div style={{ fontSize: 12, fontWeight: 500, color: "#9aa0a4", marginTop: 2 }}>
                    It won't go through until you verify your bank account. See below.
                  </div>
                </div>
              )}
              {transitTotal > 0 && (
                <div style={{ fontSize: 14, fontWeight: 700, color: "#0f766e", marginTop: 6 }}>
                  {money(transitTotal)} payment in transit — already credited above.
                  <div style={{ fontSize: 12, fontWeight: 500, color: "#5b6266", marginTop: 2 }}>
                    Bank transfers take 1–4 business days to settle. No need to pay it again.
                  </div>
                </div>
              )}
              {Number(data.partial_paid_total ?? 0) > 0 && (
                <div style={{ fontSize: 13, color: "#7dd3a8", marginTop: 6, fontWeight: 600 }}>
                  {money(data.partial_paid_total)} paid so far — thank you!
                </div>
              )}
              {justPaidPartial && (
                <div style={{ fontSize: 13, color: "#7dd3a8", marginTop: 6 }}>
                  Payment received — your balance will update once it settles (bank transfers can take 3–5 business days).
                </div>
              )}
            </div>
          )}

          {/* Finish a bank payment typed in by hand (Oct 20, 2026) */}
          {!paid && verifyPending.map((p, i) => (
            <VerifyBankCard key={`${p.made_at ?? ""}-${i}`} p={p} />
          ))}

          {/* Pay actions (only when not paid) */}
          {!paid && (
            <div style={{ marginTop: 22 }}>
              {/* Amount chooser — full balance or a partial amount */}
              <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                <button
                  onClick={() => setPayChoice("full")}
                  style={payChoice === "full" ? amountChoiceActiveStyle : amountChoiceStyle}
                >
                  <div style={{ fontWeight: 700, fontSize: 14 }}>Full balance</div>
                  <div style={{ fontSize: 13, marginTop: 2 }}>{money(balanceDue)}</div>
                </button>
                <button
                  onClick={() => setPayChoice("custom")}
                  style={payChoice === "custom" ? amountChoiceActiveStyle : amountChoiceStyle}
                >
                  <div style={{ fontWeight: 700, fontSize: 14 }}>Other amount</div>
                  <div style={{ fontSize: 13, marginTop: 2 }}>Make a partial payment</div>
                </button>
              </div>
              {payChoice === "custom" && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ position: "relative" }}>
                    <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#5b6266", fontWeight: 700 }}>$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      placeholder={`Up to ${money(balanceDue)}`}
                      style={{
                        width: "100%", boxSizing: "border-box", padding: "13px 14px 13px 28px",
                        fontSize: 16, fontWeight: 700, borderRadius: 10,
                        border: customInvalid ? "2px solid #d14d26" : "2px solid #c9ced1",
                        outline: "none", color: "#2a2e30",
                      }}
                    />
                  </div>
                  <div style={{ fontSize: 12, marginTop: 6, color: customInvalid ? "#d14d26" : "#5b6266" }}>
                    {customInvalid
                      ? `Enter an amount between $1.00 and ${money(balanceDue)}.`
                      : "Pay what you can now — the remaining balance stays due on this same link."}
                  </div>
                </div>
              )}
              {/* Method chooser — ACH (free) vs Card (3% over $1,500) */}
              <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                <button
                  onClick={() => setPayMethod("ach")}
                  style={payMethod === "ach" ? amountChoiceActiveStyle : amountChoiceStyle}
                >
                  <div style={{ fontWeight: 700, fontSize: 14 }}>🏦 Bank transfer (ACH)</div>
                  <div style={{ fontSize: 12, marginTop: 2, color: "#0e9f6e", fontWeight: 600 }}>
                    No processing fee
                  </div>
                </button>
                <button
                  onClick={() => setPayMethod("card")}
                  style={payMethod === "card" ? amountChoiceActiveStyle : amountChoiceStyle}
                >
                  <div style={{ fontWeight: 700, fontSize: 14 }}>💳 Credit / Debit card</div>
                  <div style={{ fontSize: 12, marginTop: 2, color: feeFreeForThisAmount ? "#0e9f6e" : "#b45309", fontWeight: 600 }}>
                    {feeFreeForThisAmount
                      ? "No fee on payments $1,500 or less"
                      : "3% fee on payments over $1,500"}
                  </div>
                </button>
              </div>

              {/* Total preview — payment + card fee = total charged */}
              {baseCents > 0 && (
                <div style={feePreviewStyle}>
                  <div style={{ display: "flex", justifyContent: "space-between", color: "#5b6266" }}>
                    <span>Payment</span>
                    <span>{money(baseCents / 100)}</span>
                  </div>
                  {feeCents > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", color: "#5b6266", marginTop: 4 }}>
                      <span>Card processing fee (3%)</span>
                      <span>{money(feeCents / 100)}</span>
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, color: "#2a2e30", borderTop: "1px solid #e3e6e8", paddingTop: 6, marginTop: 6 }}>
                    <span>Total charged today</span>
                    <span>{money(totalCents / 100)}</span>
                  </div>
                </div>
              )}

              <button
                onClick={handlePayOnline}
                disabled={creatingCheckout || (payChoice === "custom" && parsedCustom === null)}
                style={payOnlineBtnStyle}
              >
                {creatingCheckout
                  ? "Opening secure checkout…"
                  : payAmount !== null
                    ? `${payMethod === "card" ? "💳" : "🏦"} Pay ${money(totalCents / 100)} securely online`
                    : "💳 Enter an amount to pay online"}
              </button>
              <div style={{ fontSize: 12, color: "#5b6266", textAlign: "center", marginTop: 8 }}>
                {payMethod === "ach"
                  ? "Bank transfer (ACH) · Stripe-secured · Takes about 60 seconds"
                  : "Credit / debit card · Stripe-secured · Takes about 60 seconds"}
              </div>

              <div style={checkBlockStyle}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#5b6266", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>
                  Prefer to pay by check?
                </div>
                <div style={{ fontSize: 14, color: "#2a2e30", lineHeight: 1.6 }}>
                  Make checks payable to <b>Peach Duck Contracting Co.</b><br />
                  Memo: <b style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>{data.invoice_number ?? "Invoice"}</b> &nbsp;·&nbsp; {data.homeowner_name}<br />
                  Mail to: <b>Bardin Roofing · 225 Reformation Pkwy, Suite 200 · Canton, GA 30114</b>
                </div>
              </div>
            </div>
          )}

          {/* Download Invoice PDF button */}
          <div style={{ marginTop: 22 }}>
            <button
              onClick={handleDownloadPDF}
              disabled={generatingPdf}
              style={downloadBtnStyle}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 8, flexShrink: 0 }}>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              {generatingPdf ? "Generating invoice…" : "Download Invoice PDF"}
            </button>
          </div>

          {/* PDF preview */}
          {signedUrl && (
            <div style={{ marginTop: 22 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#5b6266", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>
                Your invoice PDF
              </div>
              <iframe
                src={signedUrl}
                title="Invoice PDF"
                style={{ width: "100%", height: 640, border: "1px solid #e3e6e8", borderRadius: 8 }}
              />
            </div>
          )}

          {/* Footer / contact */}
          <div style={contactStyle}>
            <b style={{ color: "#2a2e30" }}>Questions?</b>{" "}
            Reply to your invoice email or text us at <b>(470) 823-8816</b>. Shannon in Finance handles billing
            and responds within one business day — usually faster.
          </div>
        </div>
      </div>
      <style>{cssReset}</style>
    </div>
  );
}

function Header() {
  return (
    <div style={{ background: "#2a2e30", padding: "20px 28px", borderBottom: "4px solid #f46530" }}>
      <div style={{ fontWeight: 800, fontSize: 22, color: "#f46530", letterSpacing: -0.5 }}>BARDIN ROOFING</div>
      <div style={{ fontSize: 11, color: "#9aa0a4", marginTop: 4 }}>
        Peach Duck Contracting Co. d/b/a Bardin Roofing · (470) 823-8816 · invoices@bardinroofing.com
      </div>
    </div>
  );
}

// --- styles -----------------------------------------------------------------

// A bank payment the homeowner typed in by hand and still has to verify.
function VerifyBankCard({ p }: { p: NonNullable<ResolvedInvoice["pending_payments"]>[number] }) {
  const { lookFor, enter } = depositInstructions(p.microdeposit_type);
  const arrival = fmtVerifyDay(p.deposit_arrival_at);
  const arrived = depositShouldBeThere(p.deposit_arrival_at);
  const deadline = fmtVerifyDeadline(p.verify_deadline_at);
  const left = daysLeftLabel(daysUntil(p.verify_deadline_at));
  return (
    <div style={verifyCardStyle}>
      <div style={{ fontSize: 17, fontWeight: 800, color: "#78350f" }}>
        🏦 Action needed: finish your {money(p.amount)} bank payment
      </div>
      <div style={{ fontSize: 14, color: "#78350f", marginTop: 8, lineHeight: 1.5 }}>
        You typed in your bank account and routing numbers, so Stripe has to confirm the account
        before the payment goes through. Nothing is charged until you do.
      </div>
      <div style={{ fontSize: 14, color: "#78350f", marginTop: 8, lineHeight: 1.5 }}>
        Look in your bank account for {lookFor}. Then tap below and enter {enter}.
        {arrival && !arrived ? ` It should show up by ${arrival}.` : ""}
      </div>
      {deadline && (
        <div style={{ fontSize: 14, color: "#78350f", marginTop: 8, lineHeight: 1.5 }}>
          <b>Please finish by {deadline}{left ? ` (${left})` : ""}.</b> After that the payment is
          cancelled and you would need to pay again.
        </div>
      )}
      {p.verify_url ? (
        <a href={p.verify_url} target="_blank" rel="noopener noreferrer" style={verifyBtnStyle}>
          Verify my bank account →
        </a>
      ) : (
        <div style={{ fontSize: 13, color: "#78350f", marginTop: 10 }}>
          We're getting the verification link from Stripe. Check back here in a few minutes, or text
          us at <b>(470) 823-8816</b>.
        </div>
      )}
    </div>
  );
}

const verifyCardStyle: React.CSSProperties = {
  marginTop: 16,
  padding: "18px 20px",
  background: "#fffbeb",
  border: "2px solid #fcd34d",
  borderRadius: 12,
};
const verifyBtnStyle: React.CSSProperties = {
  display: "block",
  marginTop: 14,
  background: "#d97706",
  color: "white",
  textAlign: "center",
  textDecoration: "none",
  padding: "14px 22px",
  borderRadius: 12,
  fontSize: 16,
  fontWeight: 700,
};

const layoutStyle: React.CSSProperties = {
  minHeight: "100vh",
  background: "#faf9f7",
  padding: "40px 16px",
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
};
const cardStyle: React.CSSProperties = {
  maxWidth: 680,
  margin: "0 auto",
  background: "white",
  borderRadius: 16,
  boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
  overflow: "hidden",
};
const balanceHeroStyle: React.CSSProperties = {
  marginTop: 22,
  background: "#2a2e30",
  borderRadius: 12,
  padding: "20px 22px",
};
const paidHeroStyle: React.CSSProperties = {
  marginTop: 22,
  background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
  borderRadius: 12,
  padding: "20px 22px",
};
const amountChoiceStyle: React.CSSProperties = {
  flex: 1,
  background: "white",
  color: "#2a2e30",
  border: "2px solid #c9ced1",
  padding: "12px 14px",
  borderRadius: 12,
  cursor: "pointer",
  textAlign: "left",
};
const amountChoiceActiveStyle: React.CSSProperties = {
  ...amountChoiceStyle,
  border: "2px solid #f46530",
  background: "#fff1ea",
};
const payOnlineBtnStyle: React.CSSProperties = {
  width: "100%",
  background: "#f46530",
  color: "white",
  border: "none",
  padding: "16px 22px",
  borderRadius: 12,
  fontSize: 16,
  fontWeight: 700,
  cursor: "pointer",
  boxShadow: "0 2px 6px rgba(244,101,48,0.3)",
};
const feePreviewStyle: React.CSSProperties = {
  background: "#f3f5f6",
  border: "1px solid #e3e6e8",
  borderRadius: 10,
  padding: "12px 14px",
  fontSize: 13,
  marginBottom: 12,
};
const checkBlockStyle: React.CSSProperties = {
  marginTop: 16,
  padding: "16px 18px",
  background: "#f3f5f6",
  border: "1px solid #e3e6e8",
  borderRadius: 10,
};
const downloadBtnStyle: React.CSSProperties = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "white",
  color: "#345c72",
  border: "2px solid #345c72",
  padding: "14px 22px",
  borderRadius: 12,
  fontSize: 15,
  fontWeight: 700,
  cursor: "pointer",
  transition: "background 0.15s, color 0.15s",
};
const contactStyle: React.CSSProperties = {
  marginTop: 24,
  padding: "14px 16px",
  background: "#fff1ea",
  border: "1px solid #ff9e7a",
  borderRadius: 10,
  fontSize: 13,
  color: "#2a2e30",
  lineHeight: 1.55,
};
const cssReset = `
  body { margin: 0; padding: 0; }
  button:disabled { opacity: 0.55; cursor: not-allowed !important; }
`;
